import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Loader2, ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

type Listing = {
  id: string;
  title: string;
  price: number | null;
  price_unit: string | null;
  municipality: string | null;
  location: string | null;
  province: string | null;
  species: string | null;
  category: string | null;
  sub_category: string | null;
  description: string | null;
  delivery: string | null;
  images: string[] | null;
};

const DELIVERY_SYNONYMS: Record<string, string[]> = {
  hamta: ["hämta", "hamta", "pickup", "upphämtning", "avhamtning", "avhämtning", "själv"],
  frakt: ["frakt", "leverans", "skicka", "shipping", "transport", "hemleverans"],
};

function normalize(s: string): string {
  return s
    .toLowerCase()
    .replace(/å/g, "a")
    .replace(/ä/g, "a")
    .replace(/ö/g, "o");
}

function tokenize(q: string): string[] {
  return q
    .toLowerCase()
    .split(/[\s,]+/)
    .map((t) => t.trim())
    .filter((t) => t.length >= 2);
}

function extractPriceTokens(tokens: string[]): number[] {
  return tokens
    .map((t) => Number(t.replace(/[^\d.]/g, "")))
    .filter((n) => Number.isFinite(n) && n > 0);
}

function scoreListing(l: Listing, tokens: string[]): number {
  const haystackRaw = [
    l.title,
    l.description,
    l.species,
    l.category,
    l.sub_category,
    l.municipality,
    l.location,
    l.province,
    l.delivery,
    l.price != null ? String(l.price) : "",
    l.price_unit,
  ]
    .filter(Boolean)
    .join(" ");
  const haystack = haystackRaw.toLowerCase();
  const haystackNorm = normalize(haystackRaw);
  let score = 0;
  for (const t of tokens) {
    const tn = normalize(t);
    if (haystack.includes(t) || haystackNorm.includes(tn)) {
      score += 3;
      continue;
    }
    // Delivery synonym match
    let matched = false;
    for (const key of Object.keys(DELIVERY_SYNONYMS)) {
      if (DELIVERY_SYNONYMS[key].some((syn) => normalize(syn).includes(tn) || tn.includes(normalize(syn)))) {
        if (l.delivery && normalize(l.delivery).includes(key)) {
          score += 3;
          matched = true;
          break;
        }
      }
    }
    if (matched) continue;
    // Prefix fuzzy
    if (haystackNorm.split(/\s+/).some((w) => w.startsWith(tn) || tn.startsWith(w))) {
      score += 1;
    }
  }
  // Price proximity bonus: if any numeric token, listings with price <= token*1.2 score higher
  const prices = extractPriceTokens(tokens);
  if (prices.length > 0 && l.price != null) {
    const best = Math.min(...prices.map((p) => Math.abs(l.price! - p) / p));
    if (best <= 0.2) score += 2;
    else if (best <= 0.5) score += 1;
  }
  return score;
}

export function NavSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<Listing[]>([]);
  const [totalFound, setTotalFound] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    const tokens = tokenize(q);
    if (tokens.length === 0) {
      setResults([]);
      setTotalFound(0);
      setLoading(false);
      return;
    }
    setLoading(true);
    const handle = setTimeout(async () => {
      const priceTokens = extractPriceTokens(tokens);
      const orParts: string[] = [];
      for (const t of tokens) {
        const safe = t.replace(/[%,()]/g, "");
        orParts.push(
          `title.ilike.%${safe}%`,
          `description.ilike.%${safe}%`,
          `species.ilike.%${safe}%`,
          `category.ilike.%${safe}%`,
          `sub_category.ilike.%${safe}%`,
          `municipality.ilike.%${safe}%`,
          `location.ilike.%${safe}%`,
          `province.ilike.%${safe}%`,
          `delivery.ilike.%${safe}%`,
        );
        // Delivery synonyms
        const tn = normalize(t);
        for (const key of Object.keys(DELIVERY_SYNONYMS)) {
          if (DELIVERY_SYNONYMS[key].some((syn) => normalize(syn).includes(tn) || tn.includes(normalize(syn)))) {
            orParts.push(`delivery.ilike.%${key}%`);
          }
        }
      }
      // Numeric price tokens: include listings priced within ±50% so they can rank
      for (const p of priceTokens) {
        const low = Math.max(0, Math.floor(p * 0.5));
        const high = Math.ceil(p * 1.5);
        orParts.push(`and(price.gte.${low},price.lte.${high})`);
      }

      const { data, error } = await supabase
        .from("listings")
        .select(
          "id,title,price,price_unit,municipality,location,province,species,category,sub_category,description,delivery,images"
        )
        .eq("status", "aktiv")
        .or(orParts.join(","))
        .limit(50);
      if (!error && data) {
        const scored = (data as Listing[])
          .map((l) => ({ l, s: scoreListing(l, tokens) }))
          .filter((x) => x.s > 0)
          .sort((a, b) => b.s - a.s);
        setTotalFound(scored.length);
        setResults(scored.slice(0, 5).map((x) => x.l));
      }
      setLoading(false);
    }, 250);
    return () => clearTimeout(handle);
  }, [q]);

  function goToFullResults() {
    navigate(`/annonser?q=${encodeURIComponent(q.trim())}`, { state: { fresh: true } });
    setOpen(false);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (q.trim()) goToFullResults();
  }

  return (
    <div ref={containerRef} className="relative w-full max-w-xl">
      <form onSubmit={handleSubmit}>
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            placeholder="Sök virke, ved, kategori, ort, pris..."
            className="h-11 w-full rounded-full border border-border bg-background pl-10 pr-4 text-sm outline-none transition-colors focus:border-primary focus:ring-1 focus:ring-primary"
            aria-label="Sök annonser"
          />
          {loading && (
            <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
          )}
        </div>
      </form>

      {open && q.trim().length >= 2 && (
        <div
          className="absolute left-0 right-0 top-full mt-2 max-h-[70vh] overflow-y-auto rounded-lg border border-border bg-card shadow-lg"
          style={{ zIndex: 1100 }}
        >
          {results.length === 0 && !loading && (
            <div className="px-4 py-6 text-center text-sm text-muted-foreground">
              Inga annonser hittades.
            </div>
          )}
          {results.map((l) => {
            const img = l.images && l.images.length > 0 ? l.images[0] : null;
            const city = l.municipality || l.location || l.province || "";
            return (
              <Link
                key={l.id}
                to={`/annonser/${l.id}`}
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 border-b border-border px-3 py-2 last:border-b-0 hover:bg-secondary/60"
              >
                <div className="h-12 w-12 shrink-0 overflow-hidden rounded-md bg-muted">
                  {img && (
                    <img
                      src={img}
                      alt={l.title}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{l.title}</div>
                  {city && (
                    <div className="truncate text-xs text-muted-foreground">{city}</div>
                  )}
                </div>
                {l.price != null && (
                  <div className="shrink-0 text-sm font-semibold text-primary">
                    {Number(l.price).toLocaleString("sv-SE")} kr
                    {l.price_unit ? <span className="text-xs font-normal text-muted-foreground">/{l.price_unit}</span> : null}
                  </div>
                )}
              </Link>
            );
          })}
          {results.length > 0 && (
            <button
              type="button"
              onClick={goToFullResults}
              className="flex w-full items-center justify-center gap-2 border-t border-border bg-secondary/40 px-3 py-2.5 text-sm font-medium text-primary transition-colors hover:bg-secondary"
            >
              Visa alla{totalFound > results.length ? ` (${totalFound})` : ""}
              <ArrowRight className="h-4 w-4" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
